import type { CompaniesControllerCreateCompanyMutationRequest } from '@proposify/document-service-api-schema/dist/types/CompaniesControllerCreateCompany';
import type { CompanyResponse } from '@proposify/document-service-api-schema/dist/types/CompanyResponse';
import type { ContactResponse } from '@proposify/document-service-api-schema/dist/types/ContactResponse';
import type { ContactsControllerCreateContactMutationRequest } from '@proposify/document-service-api-schema/dist/types/ContactsControllerCreateContact';
import type { ProspectOption } from 'components/editor/SidePanel/DocumentSettings/useProspect';
import type { Contact } from 'services/entities/PersonEntity';
import { ProspectType } from 'services/repositories/interfaces/ProspectRepository';

// Composite id format used by the crm endpoints: {dataSourceType}#{tenantId}#{objectType}#{objectId}
export type CompositeEntityId = `${string}#${string}#${string}#${string}`;

export const isCrmCompositeId = (id: unknown): id is CompositeEntityId => typeof id === 'string' && id.includes('#');

export const toCompositeId = (o: { dataSourceType: string; tenantId: string; objectType: string; objectId: string }): CompositeEntityId =>
  `${o.dataSourceType}#${o.tenantId}#${o.objectType}#${o.objectId}`;

// Builds the composite id for a Proposify-native (non-CRM) company or contact, matching the
// format the CRM-linking code path expects for every entity, native or externally synced.
export const toProposifyCompositeId = (accountId: string, objectType: 'Company' | 'Client', legacyId: string | number): CompositeEntityId =>
  toCompositeId({ dataSourceType: 'Proposify', tenantId: accountId, objectType, objectId: String(legacyId) });

// A Proposify-sourced CRM composite id (Proposify#{accountId}#{objectType}#{legacyId}) embeds the
// original legacy Proposify id as its last segment. Extract it back out.
export const extractLegacyIdFromCompositeId = (compositeId: string): string => {
  return compositeId.split('#').slice(3).join('#');
};

export interface CrmCompany {
  id: CompositeEntityId;
  dataSourceType: string;
  objectType: string;
  objectId: string;
  displayableName: string;
  data: Record<string, unknown>;
}

export interface CrmContact {
  id: CompositeEntityId;
  dataSourceType: string;
  objectType: string;
  objectId: string;
  displayableName: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  data: Record<string, unknown>;
}

export type ContactDto = {
  id: string;
  account_id: string;
  first_name: string;
  last_name: string;
  email: string;
  title?: string;
  office_no?: string;
  mobile_no?: string;
  website_url?: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  state_id?: string;
  state_name?: string;
  country_id?: string;
  country_name?: string;
  zip_code?: string;
  use_company_address: boolean;
  is_primary: boolean;
  use_for_proposal_id: number;
  company_ids?: string[];
};

export type CompanyDto = {
  id: string;
  account_id: string;
  name: string;
  website_url?: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  state_id?: string;
  country_id?: string;
  country_name?: string;
  zip_code?: string;
  contact_ids?: string[];
};

export type CreateContactRequestDto = Omit<ContactDto, 'id' | 'account_id' | 'use_for_proposal_id'> & {
  use_for_proposal_id?: number;
};
export type CreateCompanyRequestDto = Omit<CompanyDto, 'id' | 'account_id' | 'country_name'>;

export const mapContactDtoToContactResponse = (contactDto: ContactDto): ContactResponse => {
  return {
    id: contactDto.id,
    accountId: contactDto.account_id,
    firstName: contactDto.first_name,
    lastName: contactDto.last_name,
    email: contactDto.email,
    title: contactDto.title,
    officeNo: contactDto.office_no,
    mobileNo: contactDto.mobile_no,
    websiteUrl: contactDto.website_url,
    address1: contactDto.address_1,
    address2: contactDto.address_2,
    city: contactDto.city,
    stateId: contactDto.state_id != null ? Number(contactDto.state_id) : undefined,
    stateName: contactDto.state_name,
    countryId: contactDto.country_id != null ? Number(contactDto.country_id) : undefined,
    countryName: contactDto.country_name,
    zipCode: contactDto.zip_code,
    useCompanyAddress: contactDto.use_company_address,
    isPrimary: contactDto.is_primary,
    companies: contactDto.company_ids?.map((companyId) => ({ id: companyId })) || [],
  };
};

export const mapCreateContactRequestToDto = (payload: ContactsControllerCreateContactMutationRequest): CreateContactRequestDto => ({
  first_name: payload.firstName,
  last_name: payload.lastName,
  email: payload.email,
  company_ids: payload.companies?.map((c) => c.id).filter((id): id is string => id != null),
  title: payload.title,
  mobile_no: payload.mobileNo,
  office_no: payload.officeNo,
  website_url: payload.websiteUrl,
  address_1: payload.address1,
  address_2: payload.address2,
  city: payload.city,
  state_id: payload.stateId != null ? String(payload.stateId) : undefined,
  state_name: payload.stateName,
  country_id: payload.countryId != null ? String(payload.countryId) : undefined,
  zip_code: payload.zipCode,
  use_company_address: payload.useCompanyAddress ?? false,
  is_primary: payload.isPrimary ?? false,
});

export const mapCompanyDtoToCompanyResponse = (companyDto: CompanyDto): CompanyResponse => {
  return {
    id: companyDto.id,
    accountId: companyDto.account_id,
    name: companyDto.name,
    websiteUrl: companyDto.website_url,
    address1: companyDto.address_1,
    address2: companyDto.address_2,
    city: companyDto.city,
    stateId: companyDto.state_id != null ? Number(companyDto.state_id) : undefined,
    countryId: companyDto.country_id != null ? Number(companyDto.country_id) : undefined,
    countryName: companyDto.country_name,
    zipCode: companyDto.zip_code,
    contacts: companyDto.contact_ids?.map((contactId) => ({ id: contactId })) || [],
  };
};

export const mapCreateCompanyRequestToDto = (payload: CompaniesControllerCreateCompanyMutationRequest): CreateCompanyRequestDto => ({
  name: payload.name,
  website_url: payload.websiteUrl,
  address_1: payload.address1,
  address_2: payload.address2,
  city: payload.city,
  state_id: payload.stateId != null ? String(payload.stateId) : undefined,
  country_id: payload.countryId != null ? String(payload.countryId) : undefined,
  zip_code: payload.zipCode,
  contact_ids: payload.contacts?.map((c) => c.id),
});

export const mapCompanyResponseToProspectOption = (company: CompanyResponse): ProspectOption => ({
  label: company.name,
  value: `${company.id}-${ProspectType.COMPANY}`,
  id: company.id,
  type: ProspectType.COMPANY,
});

export const mapContactResponseToContact = (contact: ContactResponse): Contact => ({
  id: contact.id,
  uid: `${contact.id}-contact`,
  email: contact.email ?? '',
  firstName: contact.firstName ?? '',
  lastName: contact.lastName ?? '',
  fullName: [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() || contact.email || '',
  companyId: contact.companies?.[0]?.id,
  type: 'contact' as const,
});

export const mapCrmCompanyToProspectOption = (company: CrmCompany): ProspectOption => ({
  label: company.displayableName,
  value: company.id,
  id: company.id,
  type: ProspectType.COMPANY,
});

export const mapCrmContactToContact = (contact: CrmContact): Contact => ({
  id: contact.id,
  uid: contact.id,
  email: contact.email,
  // firstName and lastName not yet available, will be added to the BFF once DT-1700 is done.
  firstName: '',
  lastName: '',
  fullName: contact.displayableName,
  type: 'contact',
});
